import { Box, Button } from '@air/zephyr';
import fuzzysearch from 'fuzzysearch';
import { useMemo, useState } from 'react';

import { AgencyCard } from '~/old-marketing/components/agency/AgencyCard/AgencyCard';
import { AgencyListFilters } from '~/old-marketing/components/agency/AgencyList/AgencyListFilters';
import { AgencyListSearch } from '~/old-marketing/components/agency/AgencyList/AgencyListSearch';
import { LimitedAgencyData } from '~/old-marketing/components/types/custom-types';
import { AgencyType } from '~/old-marketing/components/types/generated-types';

const NUMBER_PER_PAGE = 12;

interface AgencyListProps {
  agencies: LimitedAgencyData[];
  filters?: {
    companySize?: string;
    location?: string;
    type?: string;
  };
}

export const AgencyList = ({ agencies }: AgencyListProps) => {
  const [currentPage, setCurrentPage] = useState(1);

  const [searchValue, setSearchValue] = useState('');

  const [locationFilter, setLocationFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');

  const { shouldShowMore, sortedAgencies } = useMemo(() => {
    const filteredAgencies = agencies.filter((agency) => {
      if (locationFilter !== '' && !agency.location.includes(locationFilter)) {
        return false;
      }

      if (serviceFilter !== '' && !agency.types.includes(serviceFilter as AgencyType)) {
        return false;
      }

      if (sizeFilter !== '' && agency.companySize !== sizeFilter) {
        return false;
      }

      if (searchValue !== '' && !fuzzysearch(searchValue.toLowerCase(), agency.name.toLowerCase())) {
        return false;
      }

      return true;
    });

    const numberOfFilteredAgencies = filteredAgencies.length;

    return {
      shouldShowMore: numberOfFilteredAgencies > currentPage * NUMBER_PER_PAGE,
      sortedAgencies: filteredAgencies.slice(0, currentPage * NUMBER_PER_PAGE),
    };
  }, [agencies, locationFilter, currentPage, searchValue, serviceFilter, sizeFilter]);

  return (
    <Box>
      <Box tx={{ maxWidth: 694, mx: 'auto', mb: 48 }}>
        <AgencyListSearch onChange={(value) => setSearchValue(value)} value={searchValue} />
      </Box>

      <AgencyListFilters
        onChange={(values) => {
          setLocationFilter(values.location);

          setServiceFilter(values.service);

          setSizeFilter(values.size);
        }}
        values={{ location: locationFilter, service: serviceFilter, size: sizeFilter }}
      />

      <Box
        tx={{
          display: 'grid',
          gap: [16, 24],
          gridTemplateColumns: ['1fr', 'repeat(2, 1fr)', 'repeat(4, 1fr)'],
          mb: 48,
        }}
      >
        {sortedAgencies.map((agency) => {
          return (
            <AgencyCard
              facebookUrl={agency.facebookUrl}
              image={agency.logos?.[0]}
              instagramUrl={agency.instagramUrl}
              linkedInUrl={agency.linkedInUrl}
              key={agency.id}
              name={agency.name}
              slug={agency.slug}
              twitterUrl={agency.twitterUrl}
              types={agency.types}
              websiteUrl={agency.websiteUrl}
            />
          );
        })}
      </Box>

      {shouldShowMore && (
        <Box tx={{ textAlign: 'center' }}>
          <Button
            onClick={() => setCurrentPage(currentPage + 1)}
            tx={{ width: ['100%', 'unset'] }}
            variant="button-filled-grey"
          >
            Load more
          </Button>
        </Box>
      )}
    </Box>
  );
};
